import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';
import {
  reportingService,
  type StatusReport,
  type StatusReportFindingSeverity,
} from '@services/reportingService';
import { organizationService } from '@services/organizationService';
import ScrollableTableContainer from '@components/common/ScrollableTableContainer';
import { toDisplayError } from '@utils/apiError';

/**
 * REV-215 / REV-897: an organization's status report. Pick a date range and
 * get a snapshot of new and resolved findings, remediation progress, activity
 * and the tracked places with the most open findings. Org-scoped, reached from
 * the navigation for the org chosen in the header (REV-589); it began as a
 * consultant-only per-client page, retired with that persona (REV-449).
 */
export default function OrgStatusReportPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const defaultFrom = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  }, []);
  const defaultTo = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [pendingFrom, setPendingFrom] = useState(defaultFrom);
  const [pendingTo, setPendingTo] = useState(defaultTo);
  const [report, setReport] = useState<StatusReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      // The report endpoint takes the org id; the URL carries the slug.
      const org = await organizationService.getBySlug(slug);
      const r = await reportingService.getStatusReport(org.id, {
        // Dates from the picker are YYYY-MM-DD; send as ISO8601 start/end
        // of day so the backend window comparison includes the boundaries.
        from: `${from}T00:00:00.000Z`,
        to: `${to}T23:59:59.999Z`,
      });
      setReport(r);
    } catch (err) {
      setError(
        toDisplayError(err, t('statusReport.errors.fetchFailed', 'Failed to load status report'))
      );
    } finally {
      setLoading(false);
    }
  }, [slug, from, to, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyRange = () => {
    setFrom(pendingFrom);
    setTo(pendingTo);
  };

  if (loading && !report) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !report) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!report) return null;

  const sev: StatusReportFindingSeverity[] = ['critical', 'major', 'minor', 'info'];
  const { baseline, baseline_new_in_window: placeBaseline } = report.findings;
  // REV-939: findings on places scanned for the first time this window are
  // where those places start, not issues introduced — the API leaves them out
  // of its capacity comparison, and the net change does too.
  const netDelta =
    report.findings.new_in_window - placeBaseline - report.findings.resolved_in_window;
  const placeBaselineNote =
    placeBaseline === 1
      ? t(
          'statusReport.findings.placeBaselineOne',
          'Net change leaves out 1 finding from a place scanned for the first time this window — it is where that place starts.'
        )
      : report.findings.baseline_contexts_in_window === 1
        ? t('statusReport.findings.placeBaselineOnePlace', {
            count: placeBaseline,
            defaultValue:
              'Net change leaves out the {{count}} findings from a place scanned for the first time this window — they are where that place starts.',
          })
        : t('statusReport.findings.placeBaselineManyPlaces', {
            count: placeBaseline,
            places: report.findings.baseline_contexts_in_window,
            defaultValue:
              'Net change leaves out the {{count}} findings from {{places}} places scanned for the first time this window — they are where those places start.',
          });

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
        {t('statusReport.title', 'Status Report')}
      </Typography>
      <Typography variant="h6" component="p" color="text.secondary" gutterBottom>
        {report.organization.name}
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-end">
          <TextField
            type="date"
            label={t('statusReport.fromDate', 'From')}
            value={pendingFrom}
            onChange={(e) => setPendingFrom(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            label={t('statusReport.toDate', 'To')}
            value={pendingTo}
            onChange={(e) => setPendingTo(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="contained" onClick={applyRange} disabled={loading}>
            {t('statusReport.apply', 'Apply range')}
          </Button>
          {loading && <CircularProgress size={20} aria-label={t('common.loading', 'Loading...')} />}
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {report.recommendations.length > 0 && (
        <Stack spacing={1} sx={{ mb: 3 }}>
          {report.recommendations.map((r) => (
            <Alert
              key={r.code}
              severity={
                r.severity === 'high' ? 'error' : r.severity === 'medium' ? 'warning' : 'info'
              }
            >
              {r.message}
            </Alert>
          ))}
        </Stack>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" component="h2" fontWeight={600} gutterBottom>
              {t('statusReport.findings.heading', 'Findings')}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={6}>
                <Typography variant="caption" color="text.secondary">
                  {/* REV-939: in the window of the first scan, every finding
                      is where the organization started, not a new issue. */}
                  {baseline
                    ? t('statusReport.findings.foundAtBaseline', 'Found at baseline')
                    : t('statusReport.findings.newInWindow', 'New this window')}
                </Typography>
                <Typography variant="h4" component="p">
                  {report.findings.new_in_window}
                </Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="caption" color="text.secondary">
                  {t('statusReport.findings.resolvedInWindow', 'Resolved this window')}
                </Typography>
                <Typography variant="h4" component="p">
                  {report.findings.resolved_in_window}
                </Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="caption" color="text.secondary">
                  {t('statusReport.findings.openTotal', 'Currently open')}
                </Typography>
                <Typography variant="h4" component="p">
                  {report.findings.open_total}
                </Typography>
              </Grid>
              {/* REV-939: a net change needs a starting point; the baseline is it. */}
              {!baseline && (
                <Grid size={6}>
                  <Typography variant="caption" color="text.secondary">
                    {t('statusReport.findings.netDelta', 'Net change')}
                  </Typography>
                  {/* REV-897: a figure, not a heading, and named by the caption
                      above it. An aria-label here competed with the visible
                      "+3" (STRUCTURE-23). */}
                  <Typography
                    variant="h4"
                    component="p"
                    color={netDelta > 0 ? 'error.main' : 'success.main'}
                  >
                    {netDelta > 0 ? `+${netDelta}` : netDelta}
                  </Typography>
                </Grid>
              )}
            </Grid>
            {!baseline && placeBaseline > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {placeBaselineNote}
              </Typography>
            )}
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              {t('statusReport.findings.bySeverity', 'Open by severity')}
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              {sev.map((s) => (
                <Box key={s}>
                  <Typography variant="caption" color="text.secondary">
                    {t(`statusReport.findings.severity.${s}`, s)}
                  </Typography>
                  <Typography variant="h6" component="p">
                    {report.findings.open_by_severity[s]}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" component="h2" fontWeight={600} gutterBottom>
              {t('statusReport.remediation.heading', 'Remediation')}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={6}>
                <Typography variant="caption" color="text.secondary">
                  {t('statusReport.remediation.plansActive', 'Active plans')}
                </Typography>
                <Typography variant="h4" component="p">
                  {report.remediation.plans_by_status.active}
                </Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="caption" color="text.secondary">
                  {t('statusReport.remediation.itemsDone', 'Items completed')}
                </Typography>
                <Typography variant="h4" component="p">
                  {report.remediation.items_done_in_window}
                </Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="caption" color="text.secondary">
                  {t('statusReport.remediation.itemsOpen', 'Items open')}
                </Typography>
                <Typography variant="h4" component="p">
                  {report.remediation.items_by_status.open}
                </Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="caption" color="text.secondary">
                  {t('statusReport.remediation.itemsInProgress', 'Items in progress')}
                </Typography>
                <Typography variant="h4" component="p">
                  {report.remediation.items_by_status.in_progress}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" component="h2" fontWeight={600} gutterBottom>
              {t('statusReport.activity.heading', 'Activity this window')}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography variant="caption" color="text.secondary">
                  {t('statusReport.activity.notes', 'Advisory notes')}
                </Typography>
                <Typography variant="h4" component="p">
                  {report.activity.advisory_notes_in_window}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" component="h2" fontWeight={600} gutterBottom>
              {t('statusReport.organization.heading', 'Organization')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('statusReport.organization.contexts', 'Tracked places')}
            </Typography>
            <Typography variant="h4" component="p">
              {report.organization.contexts_count}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined">
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" component="h2" fontWeight={600}>
            {t('statusReport.topContexts.heading', 'Top contexts by open findings')}
          </Typography>
        </Box>
        <Divider />
        <ScrollableTableContainer>
          <Table aria-label={t('statusReport.topContexts.tableLabel', 'Top contexts table')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('statusReport.topContexts.name', 'Context')}</TableCell>
                <TableCell>{t('statusReport.topContexts.identifier', 'Identifier')}</TableCell>
                <TableCell align="right">
                  {t('statusReport.topContexts.openCount', 'Open findings')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.top_contexts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography color="text.secondary" variant="body2">
                      {t(
                        'statusReport.topContexts.empty',
                        'No open findings — nothing to prioritize.'
                      )}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {report.top_contexts.map((c) => (
                <TableRow key={c.context_id} hover>
                  <TableCell>{c.name || '—'}</TableCell>
                  <TableCell>{c.canonical_identifier || '—'}</TableCell>
                  <TableCell align="right">{c.open_count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollableTableContainer>
      </Paper>
    </Box>
  );
}
